import { motion, useReducedMotion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, type ReactNode } from 'react'

type Props = {
  badge: string
  /** 4 entrées : [Lithium, Aérosols, Inflammables, Corrosifs] — on en cycle 3 */
  classes: readonly string[]
}

const EASE = [0.22, 1, 0.36, 1] as const
const ACCENT = '#2a3ff5'

// Diamond placard path (viewBox 0 0 200 200).
const DIAMOND = 'M100 14 L186 100 L100 186 L14 100 Z'

// White class pictos, centred on (100,100). Constant accent placard underneath.
const PICTOS: ReactNode[] = [
  // Lithium — battery (svgrepo, 3-cell), centred on (100,100), white fill
  <g key="li" fill="#fff" transform="translate(100,100) scale(0.19) translate(-220,-230)">
    <path d="M434.272,178.912h-35.335v-25.553c0-20.07-16.329-36.398-36.399-36.398H36.399C16.329,116.96,0,133.288,0,153.359v153.139c0,20.07,16.329,36.399,36.399,36.399h326.139c20.07,0,36.399-16.328,36.399-36.399v-25.553h35.335c14.109,0,25.587-11.478,25.587-25.587v-50.859C459.859,190.39,448.381,178.912,434.272,178.912z M379.285,306.498c0,9.234-7.512,16.746-16.747,16.746H36.399c-9.234,0-16.747-7.513-16.747-16.746V153.359c0-9.234,7.512-16.746,16.747-16.746h326.139c9.234,0,16.747,7.513,16.747,16.746C379.285,170.382,379.285,290.499,379.285,306.498z M440.207,255.357c0,3.272-2.662,5.935-5.934,5.935h-35.335v-62.728h35.335c3.272,0,5.934,2.663,5.934,5.935V255.357z" />
    <path d="M105.793,146.408H53.388c-5.427,0-9.826,4.4-9.826,9.826v147.391c0,5.426,4.399,9.826,9.826,9.826h52.405c5.427,0,9.826-4.4,9.826-9.826V156.233C115.62,150.807,111.22,146.408,105.793,146.408z M95.967,293.798H63.214V166.06h32.753V293.798z" />
    <path d="M190.789,146.408h-52.405c-5.427,0-9.826,4.4-9.826,9.826v147.391c0,5.426,4.399,9.826,9.826,9.826h52.405c5.427,0,9.826-4.4,9.826-9.826V156.233C200.615,150.807,196.216,146.408,190.789,146.408z M180.963,293.798H148.21V166.06h32.753V293.798z" />
    <path d="M275.784,146.408h-52.405c-5.427,0-9.826,4.4-9.826,9.826v147.391c0,5.426,4.399,9.826,9.826,9.826h52.405c5.427,0,9.826-4.4,9.826-9.826V156.233C285.61,150.807,281.211,146.408,275.784,146.408z M265.958,293.798h-32.753V166.06h32.753V293.798z" />
  </g>,
  // Aérosols — spray can + nozzle + mist
  <g key="ae">
    <rect x="83" y="94" width="32" height="46" rx="8" fill="none" stroke="#fff" strokeWidth={5} />
    <path d="M90 94 v-8 h18 v8" fill="none" stroke="#fff" strokeWidth={5} strokeLinejoin="round" />
    <rect x="94" y="74" width="10" height="9" rx="2" fill="#fff" />
    <circle cx="120" cy="70" r="2.6" fill="#fff" />
    <circle cx="128" cy="78" r="2.6" fill="#fff" />
    <circle cx="121" cy="86" r="2.6" fill="#fff" />
  </g>,
  // Inflammables — flame (filled)
  <g key="fl" fill="#fff" stroke="none">
    <path d="M101 62c13 18 27 25 27 45a27 27 0 0 1-54 0c0-11 6-19 12-25 2 7 6 10 11 10-7-12 0-23 4-30z" />
  </g>,
]

export default function AdrClassDiamond({ badge, classes }: Props) {
  const reduce = useReducedMotion()
  const [i, setI] = useState(0)

  useEffect(() => {
    if (reduce) return
    const id = setInterval(() => setI((p) => (p + 1) % PICTOS.length), 2800)
    return () => clearInterval(id)
  }, [reduce])

  return (
    <div className="relative w-full rounded-2xl sm:rounded-3xl border border-ink-100 bg-gradient-to-br from-white via-accent-50/30 to-white overflow-hidden isolate aspect-square sm:aspect-[5/4]">
      {/* dot pattern */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.32]"
        style={{
          backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 80%)',
        }}
      />

      {/* breathing accent halo */}
      {reduce ? (
        <div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-56 w-56 rounded-full blur-3xl"
          style={{ background: `radial-gradient(circle, ${ACCENT}33, transparent 70%)` }}
        />
      ) : (
        <motion.div
          aria-hidden
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-56 w-56 rounded-full blur-3xl"
          style={{ background: `radial-gradient(circle, ${ACCENT}3d, transparent 70%)` }}
          animate={{ scale: [1, 1.12, 1], opacity: [0.45, 0.7, 0.45] }}
          transition={{ duration: 5.2, ease: 'easeInOut', repeat: Infinity }}
        />
      )}

      {/* badge pill */}
      <div className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-accent-200 bg-accent-50/90 px-2.5 py-1 text-[10px] sm:text-xs text-accent-800 backdrop-blur-sm">
        <span className="relative flex h-1.5 w-1.5">
          {!reduce && (
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
          )}
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-600" />
        </span>
        <span className="font-medium">{badge}</span>
      </div>

      {/* diamond + picto + name */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 px-6 pt-6 pb-5">
        <div className="relative w-[56%] max-w-[210px] aspect-square">
          <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
            <defs>
              <linearGradient id="adr-diamond-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3b5dff" />
                <stop offset="100%" stopColor="#242fd6" />
              </linearGradient>
            </defs>
            {/* constant accent placard (always full opacity → no blink) */}
            <path d={DIAMOND} fill="url(#adr-diamond-grad)" style={{ filter: 'drop-shadow(0 22px 36px rgba(36,47,214,0.30))' }} />
            <path d="M100 30 L170 100 L100 170 L30 100 Z" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth={3} />
            {/* picto — clean swap on the solid placard */}
            <AnimatePresence mode="wait" initial={false}>
              <motion.g
                key={i}
                initial={reduce ? false : { opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduce ? undefined : { opacity: 0, scale: 1.08 }}
                transition={{ duration: 0.28, ease: EASE }}
                style={{ transformOrigin: '100px 100px' }}
              >
                {PICTOS[i]}
              </motion.g>
            </AnimatePresence>
          </svg>
        </div>

        {/* class name */}
        <div className="flex h-6 items-center">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={i}
              initial={reduce ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: EASE }}
              className="text-sm sm:text-base font-semibold tracking-tight text-accent-700"
            >
              {classes[i] ?? ''}
            </motion.span>
          </AnimatePresence>
        </div>

        {/* class dots */}
        <div className="flex items-center gap-2">
          {PICTOS.map((_, d) => (
            <span
              key={d}
              className="h-1.5 rounded-full transition-all duration-300"
              style={{ width: d === i ? 18 : 6, backgroundColor: d === i ? ACCENT : '#d4d4dc' }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
